import { getTeacherImage } from "./teacherImages";
import { ONLINE_COURSE } from "./courseTypes";
import { collectScheduleEntries } from "./localSchedule";

export const NEED_CHIPS = [
  { id: "beginner", label: "Beginner Yoga" },
  { id: "private", label: "Private 1-to-1 Yoga" },
  { id: "hatha", label: "Hatha Yoga" },
  { id: "meditation", label: "Meditation" },
  { id: "ashtanga", label: "Ashtanga" },
  { id: "therapy", label: "Therapeutic / Specialized Yoga" },
  { id: "group", label: "General Yoga Practice" },
];

const FORMAT_PRIVATE = "Private 1-to-1";
const FORMAT_GROUP = "Live Group Classes";
const FORMAT_COURSE = "Online Course";
const FORMAT_WORKSHOP = "Workshop";

function splitPipeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  return String(value)
    .split(/[|,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function firstSentences(text, maxSentences = 2) {
  const cleaned = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  const parts = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned];
  return parts
    .slice(0, maxSentences)
    .map((s) => s.trim())
    .join(" ")
    .trim();
}

function extractExperienceLabel(bio, headline, certifications) {
  const haystack = [bio, headline, certifications].filter(Boolean).join(" ");
  const match =
    haystack.match(/(\d+)\+?\s*years?\s+of\s+(?:teaching\s+)?experience/i) ||
    haystack.match(/teaching\s+for\s+(\d+)\+?\s*years?/i) ||
    haystack.match(/over\s+(\d+)\+?\s*years?\s+of\s+teaching/i) ||
    haystack.match(/with\s+(\d+)\+?\s*years?\s+of\s+teaching/i) ||
    haystack.match(/since\s+(\d+)\+?\s*years?/i);
  if (!match) return "";
  const years = match[1];
  return `${years}+ years experience`;
}

function normalizeFormatLabel(format) {
  const raw = String(format || "").toLowerCase();
  if (!raw) return null;
  if (raw.includes("1:1") || raw.includes("1-to-1") || raw.includes("private")) {
    return FORMAT_PRIVATE;
  }
  if (raw.includes("workshop")) return FORMAT_WORKSHOP;
  if (raw.includes("group") || raw.includes("live online class")) return FORMAT_GROUP;
  if (raw.includes("course") || raw.includes("membership") || raw.includes("class pack")) {
    return FORMAT_COURSE;
  }
  if (raw.includes("online")) return FORMAT_GROUP;
  return null;
}

/**
 * Listing cards only show a price when the teacher has a monthly membership
 * offering with a verified amount. Per-class / workshop prices stay on the
 * detail page (avoids misleading "From ₹X / session" on one-off classes).
 */
function resolveMonthlyListingPrice(offerings) {
  const monthlyPrices = offerings
    .filter((o) => String(o.format || "").toLowerCase().includes("monthly"))
    .map((o) => {
      const fromInr = Number(o.price_inr ?? o.price_clean);
      if (Number.isFinite(fromInr) && fromInr > 0) return Math.round(fromInr);
      return null;
    })
    .filter((v) => v != null);

  if (!monthlyPrices.length) {
    return { minPriceInr: null, priceUnit: "month" };
  }

  return { minPriceInr: Math.min(...monthlyPrices), priceUnit: "month" };
}


function buildSpecializations(offerings, expertise) {
  const fromOfferings = offerings
    .map((o) => o.category || o.style)
    .filter(Boolean)
    .map((s) => String(s).trim());
  const unique = [...new Set(fromOfferings)];
  if (unique.length) return unique.slice(0, 4);

  const fromExpertise = splitPipeList(expertise)
    .filter((tag) => {
      const lower = tag.toLowerCase();
      return (
        lower.includes("yoga") ||
        lower.includes("meditation") ||
        lower.includes("pranayama") ||
        lower.includes("hatha") ||
        lower.includes("ashtanga") ||
        lower.includes("therapy") ||
        lower.includes("prenatal")
      );
    })
    .slice(0, 4);
  return fromExpertise;
}

function buildNeedTags(offerings, formats) {
  const tags = new Set();
  const hasPrivate = formats.includes(FORMAT_PRIVATE);
  const hasGroup =
    formats.includes(FORMAT_GROUP) ||
    formats.includes(FORMAT_COURSE) ||
    formats.includes(FORMAT_WORKSHOP);

  if (hasPrivate) tags.add("private");
  if (hasGroup) tags.add("group");

  for (const offering of offerings) {
    const level = String(offering.level || "").toLowerCase();
    if (level.includes("beginner")) tags.add("beginner");

    const category = String(offering.category || offering.style || "").toLowerCase();
    if (category.includes("hatha")) tags.add("hatha");
    if (category.includes("meditation")) tags.add("meditation");
    if (category.includes("ashtanga")) tags.add("ashtanga");
    if (category.includes("therapy") || category.includes("therapeutic")) tags.add("therapy");
    if (category.includes("private")) tags.add("private");
    if (category === "yoga" || category.includes("hatha") || category.includes("ashtanga")) {
      tags.add("group");
    }
  }

  return [...tags];
}

/**
 * Build a display model for one online teacher from a grouped search card + optional profile.
 */
export function buildOnlineTeacherCard(groupedCard, profile = null) {
  const slug = groupedCard?.instructor_slug || profile?.slug || "";
  const name =
    profile?.teacher_name ||
    groupedCard?.instructor_name ||
    groupedCard?.retreat_name ||
    "Yoga Teacher";

  const onlineOfferings = (
    profile?.offerings?.length
      ? profile.offerings.filter((o) => o.listing_type === ONLINE_COURSE)
      : groupedCard?.grouped_offerings || []
  ).map((o) => ({
    title: o.title || o.retreat_name,
    category: o.category || o.style,
    format: o.format || o.meals,
    level: o.level,
    price_inr: o.price_inr ?? o.price_clean,
    price_clean: o.price_clean ?? o.price_inr,
    schedule: o.schedule_json,
  }));

  const formats = [
    ...new Set(onlineOfferings.map((o) => normalizeFormatLabel(o.format)).filter(Boolean)),
  ];

  const specializations = buildSpecializations(
    onlineOfferings,
    profile?.expertise || groupedCard?.style
  );

  const headline = profile?.headline || groupedCard?.description || "";
  const bio = profile?.bio || "";
  const intro = firstSentences(headline) || firstSentences(bio, 2);
  const certifications = profile?.certifications || "";
  const experienceLabel = extractExperienceLabel(bio, headline, certifications);
  const languages = splitPipeList(profile?.languages);
  const { minPriceInr, priceUnit } = resolveMonthlyListingPrice(onlineOfferings);
  const needTags = buildNeedTags(onlineOfferings, formats);
  const scheduleEntries = collectScheduleEntries(onlineOfferings);

  const photo = getTeacherImage({
    instructor_slug: slug,
    teacher_slug: slug,
    teacher_id: profile?.teacher_id || groupedCard?.instructor_id,
    profile_image: profile?.profile_image || groupedCard?.thumbnail_url,
  });

  return {
    slug,
    name,
    photo,
    specializations,
    intro,
    experienceLabel,
    certifications: certifications
      ? certifications.split("|").map((c) => c.trim()).filter(Boolean).slice(0, 2)
      : [],
    languages,
    formats,
    minPriceInr,
    priceUnit,
    needTags,
    scheduleEntries,
    profilePath: slug ? `/online-courses/instructor/${slug}` : null,
  };
}

export function getAvailableNeedChips(teachers) {
  const present = new Set();
  for (const teacher of teachers) {
    for (const tag of teacher.needTags || []) present.add(tag);
  }
  return NEED_CHIPS.filter((chip) => present.has(chip.id));
}

export function filterTeachersByNeed(teachers, needId) {
  if (!needId) return teachers;
  return teachers.filter((t) => (t.needTags || []).includes(needId));
}
